import {
  ArrowLeftOutlined,
  BellOutlined,
  CompressOutlined,
  ExpandOutlined,
  ExportOutlined,
  LockOutlined,
  SearchOutlined,
  TeamOutlined
} from "@ant-design/icons"
import { Alert, Avatar, Button, Dropdown, Flex, List, Popover, Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type { Conversation, NotificationLevel } from "../types/messaging.types.ts"

const VISIBILITY_STYLE: Record<
  Conversation["visibility"],
  { color: string; icon: React.ReactNode }
> = {
  internal: { color: "default", icon: <TeamOutlined /> },
  includes_subcontractors: { color: "gold", icon: <TeamOutlined /> },
  customer: { color: "blue", icon: <ExportOutlined /> },
  private: { color: "purple", icon: <LockOutlined /> }
}

interface ConversationHeaderProps {
  conversation: Conversation
  showBack: boolean
  onBack: () => void
  onNotificationLevelChange: (level: NotificationLevel) => void
  onToggleSearch?: () => void
  isConnected: boolean
  expanded?: boolean
  /** Absent where the chat already owns the width, as in the customer portal. */
  onToggleExpand?: () => void
}

/**
 * The open conversation's own context, on one compact line.
 *
 * Deliberately *not* the project name and address — the page header above
 * already carries those, and repeating them gave the screen two titles.
 */
export function ConversationHeader({
  conversation,
  showBack,
  onBack,
  onNotificationLevelChange,
  onToggleSearch,
  isConnected,
  expanded = false,
  onToggleExpand
}: ConversationHeaderProps) {
  const { t } = useTranslation()

  const levels: Array<{ key: NotificationLevel; label: string }> = [
    { key: "all", label: t("messages.settings.all", "All messages") },
    { key: "mentions", label: t("messages.settings.mentions", "Mentions only") },
    { key: "muted", label: t("messages.settings.muted", "Muted") }
  ]

  const visibility = VISIBILITY_STYLE[conversation.visibility]

  const participantList = (
    <div className={"w-72"}>
      {conversation.kind === "project_team" && (
        <Alert
          type={"info"}
          showIcon
          className={"mb-2"}
          message={t(
            "messages.historyNotice",
            "Anyone added to this conversation can read its full history, including messages sent before they joined."
          )}
        />
      )}
      <List
        className={"max-h-80 overflow-y-auto"}
        dataSource={conversation.participants}
        renderItem={(person) => (
          <List.Item key={person.id}>
            <List.Item.Meta
              avatar={
                <Avatar src={person.avatar || undefined} size={28}>
                  {person.full_name?.[0]}
                </Avatar>
              }
              title={<span className={"text-sm"}>{person.full_name}</span>}
              description={<span className={"text-xs"}>{person.role_label}</span>}
            />
          </List.Item>
        )}
      />
    </div>
  )

  return (
    <div
      className={"shrink-0 border-b border-slate-200 bg-white px-4 py-2.5"}
      data-testid={"conversation-header"}
    >
      <Flex align={"center"} justify={"space-between"} gap={"small"}>
        <Flex align={"center"} gap={8} className={"min-w-0 flex-1"}>
          {showBack && (
            <Button
              type={"text"}
              size={"small"}
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              aria-label={t("messages.back", "Back to conversations")}
              className={"lg:hidden"}
            />
          )}
          {/* A floor, so the title cannot be squeezed out of existence by the
              count and the visibility tag beside it — which is what happened
              on a phone before the tag was allowed to shrink. */}
          <Typography.Text
            strong
            ellipsis
            className={"min-w-[3.5rem] shrink text-[15px]"}
          >
            {conversation.title}
          </Typography.Text>

          {/* The count is the control: clicking it opens the authorized list. */}
          <Popover
            trigger={"click"}
            title={t("messages.participants", "Participants")}
            content={participantList}
          >
            <button
              type={"button"}
              data-testid={"participant-count"}
              className={
                "shrink-0 text-xs text-slate-500 underline-offset-2 hover:underline"
              }
            >
              {t("messages.participantCount", {
                count: conversation.participants.length,
                defaultValue_one: "{{count}} participant",
                defaultValue_other: "{{count}} participants"
              })}
            </button>
          </Popover>

          {/* Shrinkable and truncating rather than fixed: this label says who
              can read the room, so it is never dropped on a narrow screen —
              but at `shrink-0` it ran underneath the actions instead. */}
          <Tag
            color={visibility.color}
            icon={visibility.icon}
            className={
              "m-0 flex min-w-0 shrink items-center overflow-hidden text-[11px]"
            }
            data-testid={"visibility-label"}
          >
            {/* `min-w-0` on the span too: as a flex child its default minimum
                is its content width, so without it the text spills past the
                tag's own box instead of ellipsing inside it. */}
            <span className={"min-w-0 truncate"}>{conversation.visibility_label}</span>
          </Tag>

          {!isConnected && (
            <Tag color={"orange"} className={"m-0 shrink-0 text-[11px]"}>
              {t("messages.reconnecting", "Reconnecting…")}
            </Tag>
          )}
        </Flex>

        <Flex align={"center"} gap={2} className={"shrink-0"}>
          {/* Labelled, not an icon alone: the way back out of full width has to
              be as findable as the way in, and an unlabelled arrows glyph is
              not. The label is also what the pressed state is announced on. */}
          {onToggleExpand && (
            <Button
              type={"text"}
              size={"small"}
              icon={expanded ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={onToggleExpand}
              data-testid={"expand-chat"}
              aria-pressed={expanded}
              className={"hidden lg:inline-flex"}
            >
              <span className={"text-xs"}>
                {expanded
                  ? t("messages.collapseChat", "Exit full width")
                  : t("messages.expandChat", "Expand chat")}
              </span>
            </Button>
          )}
          {onToggleSearch && (
            <Button
              type={"text"}
              size={"small"}
              icon={<SearchOutlined />}
              onClick={onToggleSearch}
              aria-label={t("messages.searchInConversation", "Search in conversation")}
            />
          )}
          <Dropdown
            trigger={["click"]}
            menu={{
              selectedKeys: [conversation.notification_level],
              items: levels.map((level) => ({ key: level.key, label: level.label })),
              onClick: ({ key }) => onNotificationLevelChange(key as NotificationLevel)
            }}
          >
            <Button
              type={"text"}
              size={"small"}
              icon={<BellOutlined />}
              aria-label={t("messages.settings.title", "Notification settings")}
            />
          </Dropdown>
        </Flex>
      </Flex>
    </div>
  )
}
